import { useEffect, useRef, useState } from "react";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useUser, useAuth } from "@clerk/react";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { InstallBanner } from "@/components/InstallBanner";
import { queryClient } from "@/lib/queryClient";
import { useGetMyProfile, setAuthTokenGetter } from "@workspace/api-client-react";
import HomePage from "@/pages/HomePage";
import TendersPage from "@/pages/TendersPage";
import TenderDetailPage from "@/pages/TenderDetailPage";
import PricingPage from "@/pages/PricingPage";
import DashboardPage from "@/pages/DashboardPage";
import ProfilePage from "@/pages/ProfilePage";
import AdminTendersPage from "@/pages/admin/AdminTendersPage";
import AdminSchoolsPage from "@/pages/admin/AdminSchoolsPage";
import AdminSchoolsLinksPage from "@/pages/admin/AdminSchoolsLinksPage";
import AdminSchoolsDirecturlPage from "@/pages/admin/AdminSchoolsDirecturlPage";
import AdminMembersPage from "@/pages/admin/AdminMembersPage";
import AdminEmailComposePage from "@/pages/admin/AdminEmailComposePage";
import AdminEmailTemplatesPage from "@/pages/admin/AdminEmailTemplatesPage";
import AdminEmailTemplateEditPage from "@/pages/admin/AdminEmailTemplateEditPage";
import AdminSchoolsUpdatePage from "@/pages/admin/AdminSchoolsUpdatePage";
import AdminGuidesPage from "@/pages/admin/AdminGuidesPage";
import AdminMetaPage from "@/pages/admin/AdminMetaPage";
import AdminAiAssistantPage from "@/pages/admin/AdminAiAssistantPage";
import AdminAiRecordPage from "@/pages/admin/AdminAiRecordPage";
import SchoolsPage from "@/pages/SchoolsPage";
import MarketPage from "@/pages/MarketPage";
import WeeklyExamplePage from "@/pages/WeeklyExamplePage";
import TipsPage from "@/pages/TipsPage";
import WelcomePage from "@/pages/WelcomePage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import TenderGuidePage from "@/pages/TenderGuidePage";
import FaqPage from "@/pages/FaqPage";
import GuidesPage from "@/pages/GuidesPage";
import GuideArticlePage from "@/pages/GuideArticlePage";
import ContactPage from "@/pages/ContactPage";
import FavouritesPage from "@/pages/FavouritesPage";
import NotFound from "@/pages/not-found";

const clerkPubKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
// Do NOT use a proxy URL. The external Clerk instance (ins_3DLbdy7xtxPLRkqD22PN67zhsji)
// uses clerk.skoltend.com as its FAPI directly. A proxy URL causes Clerk to route
// requests to a different (Replit-managed) Clerk instance via the Clerk-Proxy-Url
// header, breaking Google SSO with "failed security validations".
const clerkProxyUrl = undefined;
const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

function ClerkNotLoadedFallback({ action }: { action: "sign-in" | "sign-up" }) {
  const targetPath = action === "sign-in" ? "/sign-in" : "/sign-up";
  const productionUrl = `https://www.skoltend.com${targetPath}`;

  return (
    <div className="text-center max-w-sm mx-auto">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <svg className="w-6 h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
        </svg>
      </div>
      <h2 className="text-lg font-semibold mb-2">
        {action === "sign-in" ? "登入" : "註冊"}
      </h2>
      <p className="text-sm text-muted-foreground mb-6">
        此預覽環境不支援登入。請前往正式網站操作。
      </p>
      <a
        href={productionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
      >
        前往 www.skoltend.com {action === "sign-in" ? "登入" : "註冊"}
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
      </a>
    </div>
  );
}

function SignInPage() {
  const { isLoaded } = useUser();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 py-12">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {timedOut && !isLoaded ? (
        <ClerkNotLoadedFallback action="sign-in" />
      ) : (
        <SignIn routing="path" path={`${basePath}/sign-in`} signUpUrl={`${basePath}/sign-up`} fallbackRedirectUrl="/" />
      )}
    </div>
  );
}

function SignUpPage() {
  const { isLoaded } = useUser();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setTimedOut(true), 4000);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-secondary/30 py-12">
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {timedOut && !isLoaded ? (
        <ClerkNotLoadedFallback action="sign-up" />
      ) : (
        <SignUp routing="path" path={`${basePath}/sign-up`} signInUrl={`${basePath}/sign-in`} fallbackRedirectUrl="/" />
      )}
    </div>
  );
}

function HomeRedirect() {
  return <HomePage />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Show when="signed-in">{children}</Show>
      <Show when="signed-out"><Redirect to="/sign-in" /></Show>
    </>
  );
}

const ADMIN_EMAIL = "pcbuyandsale@yahoo.com.hk";

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded, user } = useUser();
  if (!isLoaded) return null;
  if (!isSignedIn) return <Redirect to="/sign-in" />;
  const email = user.primaryEmailAddress?.emailAddress ?? "";
  if (email !== ADMIN_EMAIL) return <Redirect to="/" />;
  return <>{children}</>;
}

function ClerkAuthSetup() {
  const { getToken, isLoaded, isSignedIn } = useAuth();
  const { user } = useUser();
  const [, setLocation] = useLocation();
  const memberSyncedRef = useRef(false);
  const welcomeCheckedRef = useRef(false);

  useEffect(() => {
    if (!isLoaded) return;
    if (isSignedIn && user) {
      setAuthTokenGetter(() => getToken());
      // Fire once per session to ensure member record exists in DB
      if (!memberSyncedRef.current) {
        memberSyncedRef.current = true;
        getToken().then(token => {
          if (token) {
            fetch("/api/members/me", {
              headers: { Authorization: `Bearer ${token}` },
            }).catch(() => {});
          }
        });
      }
      // First-login welcome redirect: send to Dashboard once per user account
      if (!welcomeCheckedRef.current) {
        welcomeCheckedRef.current = true;
        const key = `skoltend_welcomed_${user.id}`;
        if (!localStorage.getItem(key)) {
          localStorage.setItem(key, "1");
          setLocation("/welcome");
        }
      }
    } else {
      setAuthTokenGetter(null);
      memberSyncedRef.current = false;
      welcomeCheckedRef.current = false;
    }
  }, [isLoaded, isSignedIn, user, getToken, setLocation]);

  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      // Clear cache whenever auth state changes, INCLUDING the first load
      // (undefined → userId). This ensures queries fired before Clerk loaded
      // (without auth token) get re-fetched once the session is confirmed.
      if (prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

const CANONICAL_BASE = "https://www.skoltend.com";
const PUBLIC_PATHS: Record<string, string> = {
  "/": "/",
  "/tenders": "/tenders",
  "/schools": "/schools",
  "/pricing": "/pricing",
  "/market": "/market",
  "/tips": "/tips",
  "/privacy": "/privacy",
  "/terms": "/terms",
  "/contact": "/contact",
  "/tender-guide": "/tender-guide",
  "/faq": "/faq",
  "/guides": "/guides",
};

function CanonicalUrl() {
  const [location] = useLocation();
  const pathOnly = location.split("?")[0].split("#")[0];

  let canonical: string | null = null;
  if (PUBLIC_PATHS[pathOnly] !== undefined) {
    canonical = CANONICAL_BASE + PUBLIC_PATHS[pathOnly];
  } else if (pathOnly.startsWith("/tenders/")) {
    // Only set canonical for slug-style URLs (e.g. /tenders/123-title).
    // Plain numeric IDs (e.g. /tenders/123) redirect to the slug URL;
    // the canonical is set by TenderDetailPage's SeoHead after redirect.
    const tenderParam = pathOnly.slice("/tenders/".length);
    if (!/^\d+$/.test(tenderParam)) {
      canonical = CANONICAL_BASE + pathOnly;
    }
  } else if (pathOnly.startsWith("/guides/")) {
    canonical = CANONICAL_BASE + pathOnly;
  }

  if (!canonical) return null;
  return (
    <Helmet>
      <link rel="canonical" href={canonical} />
    </Helmet>
  );
}

function AppLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
      <InstallBanner />
    </div>
  );
}

function Router() {
  return (
    <AppLayout>
      <CanonicalUrl />
      <Switch>
        <Route path="/" component={HomeRedirect} />
        <Route path="/sign-in" component={SignInPage} />
        <Route path="/sign-in/:rest*" component={SignInPage} />
        <Route path="/sign-up" component={SignUpPage} />
        <Route path="/sign-up/:rest*" component={SignUpPage} />
        <Route path="/tenders" component={TendersPage} />
        <Route path="/tenders/:id" component={TenderDetailPage} />
        <Route path="/schools" component={SchoolsPage} />
        <Route path="/pricing" component={PricingPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/contact" component={ContactPage} />
        <Route path="/market" component={MarketPage} />
        <Route path="/weeklyexample" component={WeeklyExamplePage} />
        <Route path="/tips" component={TipsPage} />
        <Route path="/tender-guide" component={TenderGuidePage} />
        <Route path="/faq" component={FaqPage} />
        <Route path="/guides" component={GuidesPage} />
        <Route path="/guides/:slug" component={GuideArticlePage} />
        <Route path="/welcome" component={WelcomePage} />
        <Route path="/dashboard">
          <ProtectedRoute><DashboardPage /></ProtectedRoute>
        </Route>
        <Route path="/favourites">
          <ProtectedRoute><FavouritesPage /></ProtectedRoute>
        </Route>
        <Route path="/profile">
          <ProtectedRoute><ProfilePage /></ProtectedRoute>
        </Route>
        <Route path="/admin/tenders">
          <AdminRoute><AdminTendersPage /></AdminRoute>
        </Route>
        <Route path="/admin/schools">
          <AdminRoute><AdminSchoolsPage /></AdminRoute>
        </Route>
        <Route path="/admin/schools_links">
          <AdminRoute><AdminSchoolsLinksPage /></AdminRoute>
        </Route>
        <Route path="/admin/schools_directurl">
          <AdminRoute><AdminSchoolsDirecturlPage /></AdminRoute>
        </Route>
        <Route path="/admin/members/:id/email">
          <AdminRoute><AdminEmailComposePage /></AdminRoute>
        </Route>
        <Route path="/admin/members">
          <AdminRoute><AdminMembersPage /></AdminRoute>
        </Route>
        <Route path="/admin/email-templates/:id/edit">
          <AdminRoute><AdminEmailTemplateEditPage /></AdminRoute>
        </Route>
        <Route path="/admin/email-templates/new">
          <AdminRoute><AdminEmailTemplateEditPage /></AdminRoute>
        </Route>
        <Route path="/admin/email-templates">
          <AdminRoute><AdminEmailTemplatesPage /></AdminRoute>
        </Route>
        <Route path="/admin/schools_update">
          <AdminRoute><AdminSchoolsUpdatePage /></AdminRoute>
        </Route>
        <Route path="/admin/guides">
          <AdminRoute><AdminGuidesPage /></AdminRoute>
        </Route>
        <Route path="/admin/meta">
          <AdminRoute><AdminMetaPage /></AdminRoute>
        </Route>
        <Route path="/admin/ai-assistant">
          <AdminRoute><AdminAiAssistantPage /></AdminRoute>
        </Route>
        <Route path="/admin/ai-record">
          <AdminRoute><AdminAiRecordPage /></AdminRoute>
        </Route>
        <Route path="/admin">
          <AdminRoute><AdminTendersPage /></AdminRoute>
        </Route>
        <Route component={NotFound} />
      </Switch>
    </AppLayout>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      signInFallbackRedirectUrl="/"
      signUpFallbackRedirectUrl="/"
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
      appearance={{
        variables: {
          colorPrimary: "#2a6f4f",
          colorBackground: "#f8fdf9",
          colorForeground: "#1a2e22",
          colorMutedForeground: "#4a7a5e",
          colorDanger: "#dc2626",
          borderRadius: "0.5rem",
          fontFamily: "inherit",
        },
        elements: {
          card: "shadow-md border border-[#d1e8da]",
          headerTitle: "text-[#1a2e22] font-bold",
          headerSubtitle: "text-[#4a7a5e]",
          formButtonPrimary: "bg-[#2a6f4f] hover:bg-[#22593f] text-white",
          footerActionLink: "text-[#2a6f4f] hover:text-[#22593f]",
          identityPreviewEditButton: "text-[#2a6f4f]",
          formFieldInput: "border-[#c1dcc9] focus:border-[#2a6f4f] focus:ring-[#2a6f4f]",
        },
      }}
    >
      <QueryClientProvider client={queryClient}>
        <ClerkAuthSetup />
        <ClerkQueryClientCacheInvalidator />
        <TooltipProvider>
          <Router />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <WouterRouter base={basePath}>
        <ClerkProviderWithRoutes />
      </WouterRouter>
    </HelmetProvider>
  );
}

export default App;
